import { Component, EventListener, findComponents, startComponents, stopComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import '@swisslife/patterns-base/src/molecules/list-filter/js/list-filter';
import '../../../molecules/teaser-row/js/teaser-row';
import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';
import { dispatchCustomEvent, EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';

export interface IConfiguration {
	serviceUri: string;
	serviceUriSuffix: string;
	pagePrefix: string;
}

enum States {
	LoaderVisible = 'state-a-loader--visible',
	Error = 'state-o-video-hub--error',
	PreInit = 'state-o-video-hub--pre-init',
}

enum Selectors {
	Loader = '.js-a-loader',
	VideoHubTeaser = '.js-o-video-hub__teaser',
	ListOutlet = '.js-o-video-hub__outlet',
	ResultTitle = '.js-o-video-hub__result-title',
	ResultCount = '.js-o-video-hub__result-count',
	LoadMore = '.js-o-video-hub__load-more',
	LoadMoreButton = '.js-o-video-hub__load-more-button',
	Fragment = '.js-o-video-hub__fragment',
}

enum Actions {
	Append = 'append',
	Replace = 'replace',
}

@Component('VideoHub')
export class VideoHub extends GondelJqueryComponent {
	private $loader: JQuery<HTMLElement>;
	private $listOutlet: JQuery<HTMLElement>;
	private $resultCount: JQuery<HTMLElement>;
	private $videoHubTeaser: JQuery<HTMLElement>;
	private $loadMore: JQuery<HTMLElement>;
	private configuration: IConfiguration;
	private currentPage: number = 0;
	private resultCount: number = 0;
	private hasLoadMore: boolean = false;

	public start() {
		this.$loader = $(Selectors.Loader, this.$ctx);
		this.$listOutlet = $(Selectors.ListOutlet, this.$ctx);
		this.$videoHubTeaser = $(Selectors.VideoHubTeaser, this.$ctx);
		this.$resultCount = $(Selectors.ResultCount, this.$ctx);
		this.$loadMore = $(Selectors.LoadMore, this.$ctx);

		this.configuration = {
			serviceUri: this.$listOutlet.data('service-uri'),
			serviceUriSuffix: this.$listOutlet.data('service-uri-suffix') || '',
			pagePrefix: this.$listOutlet.data('page-prefix'),
		};

		this.currentPage = this.$listOutlet.data('current-page');
		this.resultCount = this.$listOutlet.data('result-count');
		this.hasLoadMore = this.$ctx.data('has-load-more') || false;

		$(window).on('popstate.videoHub', this.handleHistoryPopState.bind(this));
	}

	public sync() {
		this.setResultCount(this.resultCount);
		this.toggleLoadMore(this.hasLoadMore);
		this.$ctx.removeClass(States.PreInit);
	}

	@EventListener('click', Selectors.LoadMoreButton)
	public _handleLoadMoreButtonClick(): void {
		this.currentPage++;
		this.getResults(Actions.Append, true);
	}

	private generateRequestUrl(): string {
		const urlArray: string[] = [];

		urlArray.push(this.configuration.serviceUri);
		urlArray.push(`${this.configuration.pagePrefix}${this.currentPage}`);
		urlArray.push(this.configuration.serviceUriSuffix);

		return urlArray.join('.');
	}

	private generateUrlFragmentFilterState(): string {
		const urlArray: string[] = [];

		urlArray.push(`${this.configuration.pagePrefix}${this.currentPage}`); // e.g. 'page-0'
		urlArray.push(this.configuration.serviceUriSuffix);

		return urlArray.join('.');
	}

	private generateUnfilteredUrl(): string {
		const urlPathParts = document.location.href.split('/');
		const lastSegment = urlPathParts.pop();
		const restSegment = urlPathParts.join('/');
		let urlFragmentBase = '';

		if (typeof lastSegment !== 'undefined') {
			/* this separating string occurs once in agreed url pattern ->
			 * 1st part contains page name, 2nd the filter state (with paging and serviceUriSuffix)
			 */
			const partsLastSegment = lastSegment.split(`.${this.configuration.pagePrefix}`);
			const partsLastSegmentBase = partsLastSegment[0];

			if (typeof partsLastSegmentBase !== 'undefined') {
				const partsLastSegmentWithoutSuffix = partsLastSegmentBase.replace(
					`.${this.configuration.serviceUriSuffix}`,
					''
				); // strip out serviceUriSuffix
				urlFragmentBase = partsLastSegmentWithoutSuffix;
			}
		}

		return `${restSegment}/${urlFragmentBase}`;
	}

	private async getResults(action: Actions, updateHistory: boolean) {
		try {
			this.toggleLoadingState(true);

			// fetch but wait for 150 ms minimum (prevent too fast update / ui flickering)
			const fragment = (await Promise.all([ajax<string>(this.generateRequestUrl()), this.wait(150)]))[0];
			this.parseResult(fragment);

			// reflect filter state in url
			const urlFragmentFilterState = this.generateUrlFragmentFilterState(); // e.g. 'page-0.html'
			const urlFragmentUnfiltered = this.generateUnfilteredUrl();

			if (updateHistory) {
				this.updateHistory(document.title, `${urlFragmentUnfiltered}.${urlFragmentFilterState}`);
			}

			if (action === Actions.Replace) {
				stopComponents(this.$listOutlet);
				this.$listOutlet.empty();
			}

			this.$ctx.removeClass(States.Error);
			this.$listOutlet.append(fragment);
			startComponents(this.$listOutlet.find(`${Selectors.Fragment}:last-child`));
		} catch (error) {
			this.resultCount = 0;
			this.$listOutlet.empty();
			this.hasLoadMore = false;
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$ctx, EventNames.NetworkError, { statusCode });
			this.showErrorMessage();
		}

		this.toggleVideoHubTeaser();
		this.toggleLoadingState(false);
		this.toggleLoadMore(this.hasLoadMore);
	}

	private handleHistoryPopState(event): void {
		// get original event since jQuery normalizes the event
		const popStateEvent = event.originalEvent;

		if (popStateEvent.state !== null) {
			const eventData = popStateEvent.state;

			this.resultCount = eventData.resultCount;
			this.currentPage = eventData.currentPage;

			// history popState is always Actions.replace because we do push load more as a new history event
			this.getResults(Actions.Replace, false);
		} else {
			window.location.reload();
		}
	}

	private parseResult(fragment: string): void {
		const $fragment = $(fragment);

		this.hasLoadMore = $fragment.data('has-load-more');
		this.currentPage = $fragment.data('current-page');
		this.resultCount = $fragment.data('result-count');
	}

	private setResultCount(resultCount: number): void {
		this.$resultCount.text(resultCount);
	}

	private showErrorMessage(): void {
		this.$ctx.addClass(States.Error);
	}

	private toggleLoadingState(newState: boolean): void {
		if (newState) {
			this.$loader.addClass(States.LoaderVisible);
			this.$loadMore.attr('disabled', 'disabled');
		} else {
			this.$loader.removeClass(States.LoaderVisible);
			this.$loadMore.removeAttr('disabled');
		}
	}

	private toggleLoadMore(newState: boolean): void {
		if (newState) {
			this.$loadMore.slideDown();
		} else {
			this.$loadMore.slideUp();
		}
	}

	private toggleVideoHubTeaser(): void {
		this.$videoHubTeaser.slideDown();
	}

	private updateHistory(title: string, url: string): void {
		if (window.history) {
			// update uri and page title
			history.pushState(
				{
					resultCount: this.resultCount,
					currentPage: this.currentPage,
				},
				title,
				url
			);
		}

		// set browser tab title even if it's not supported by pushState
		$(document).prop('title', title);
	}

	private wait(delay: number) {
		return new Promise((receive) => setTimeout(receive, delay));
	}
}
